import { useContext, useState } from "react";
import Form from "react-bootstrap/Form";
import { Modal } from "react-bootstrap";
import { DataContext } from "../DataContext";
import { Wrapper, Button, PageTemplate, HeaderTemplate, CustomerShipping } from "../styles";
import { getCustomerOrder, updateStatus } from "../api/order";
import { ViewReturnsTemplate } from "../templates/ViewReturnsTemplate";
import { convertDate, hasReturnWindowExpired } from "../utils";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { ASSET_URL } from "../constants";

const OrderLookup = () => {
  const navigate = useNavigate();
  const data = useContext(DataContext);

  const [order, setOrder] = useState(null);
  const [showReturnsModal, setShowReturnsModal] = useState(false);
  const [orderSearch, setOrderSearch] = useState("");
  const [orderEmail, setOrderEmail] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  const [formSubmitted, setFormSubmitted] = useState(false);

  const formatStatus = (status) => {
    return status?.replace(/-/g, " ");
  };

  const getPaymentStatus = (order) => {
    if (["pending", "paid"].includes(order.paymentStatus)) return order.paymentStatus;
    if (order.status === "pending") return "pending";
    return "paid";
  };

  const getFulfillmentStatus = (order) => {
    if (["pending", "in-progress", "shipped", "completed", "cancelled"].includes(order.fulfillmentStatus)) return order.fulfillmentStatus;
    if (order.fulfillmentStatus === "returned") return "completed";
    if (["in-progress", "shipped", "completed", "cancelled"].includes(order.status)) return order.status;
    if (order.status === "return") return "completed";
    return "pending";
  };

  const getOrder = async () => {
    const order = await getCustomerOrder(orderSearch, orderEmail);
    setOrder(order);
    setFormSubmitted(true);
  };

  const handleShowCancelModal = () => {
    setShowCancelModal(true);
  };

  const handleOrderCancellation = async () => {
    try {
      await updateStatus({
        orderNumber: order.orderNumber,
        fulfillmentStatus: "cancelled",
      });
      setShowCancelConfirmation(true);
      await getOrder();
    } catch (error) {
      console.error(error);
      toast.error(error.message);
    }
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    let errorMessage;

    if (!orderEmail) {
      errorMessage = "Enter Email Address";
    }

    if (!orderSearch) {
      errorMessage = "Enter Order Number";
    }

    if (orderSearch && orderEmail) {
      try {
        await getOrder();
      } catch (error) {
        console.error(error.message);
        toast.error(error.message);
      }
    } else {
      toast.error(errorMessage);
    }
  };

  const handleViewReturns = () => {
    setShowReturnsModal(true);
  };

  const showCreateReturnButton = (order) => {
    const pricingProductsForReturn = () => {
      if (order.returns.length === 0) return true;
      return order.returns[order.returns.length - 1].products.find((product) => {
        return product.returnType === "Keep";
      });
    };
    return (
      !hasReturnWindowExpired(order.updatedAt) &&
      getFulfillmentStatus(order) === "completed" &&
      pricingProductsForReturn()
    );
  };

  return (
    <>
      <HeaderTemplate className="narrow">
        <div className="header">
          <div className="title">
            <h1>Order Lookup</h1>
          </div>
        </div>
      </HeaderTemplate>
      <PageTemplate className="narrow order-lookup">
        <div>
          <form className="order-search" onSubmit={handleSearchSubmit}>
            <Form.Group className="form-group">
              <Form.Control
                type="text"
                onChange={(e) => setOrderSearch(e.target.value)}
                name="orderSearch"
                id="orderSearch"
                placeholder="Order Number"
                value={orderSearch}
              />
            </Form.Group>
            <Form.Group className="form-group">
              <Form.Control
                type="text"
                onChange={(e) => setOrderEmail(e.target.value)}
                name="orderEmail"
                id="orderEmail"
                placeholder="Email"
                value={orderEmail}
              />
            </Form.Group>
            <Button type="submit">Search</Button>
          </form>
          {formSubmitted &&
            (order ? (
              <div className="order-lookup-result">
                <div>
                  <div className="order-history-row" key={order.orderNumber}>
                    <div className="order-history-details">
                      <div>
                        <div className="label">Order</div>
                        <div className="value">{order.orderNumber}</div>
                      </div>
                      <div>
                        <div className="label">Payment</div>
                        <div className={`status ${getPaymentStatus(order).toLowerCase()}`}>
                          <span>{formatStatus(getPaymentStatus(order))}</span>
                        </div>
                      </div>
                      <div>
                        <div className="label">Fulfillment</div>
                        <div className={`status ${getFulfillmentStatus(order).toLowerCase()}`}>
                          <span>{formatStatus(getFulfillmentStatus(order))}</span>
                        </div>
                      </div>
                      <div>
                        <div className="label">Date</div>
                        <div className="value">{convertDate(order.createdAt)}</div>
                      </div>
                    </div>

                    <div className="order-history-buttons">
                      <Button
                        className="order-history-link clear"
                        onClick={() => navigate("/support", { state: { orderNumber: order.orderNumber, previousUrl: "/order-lookup" } })}
                      >
                        Support
                      </Button>
                      {getPaymentStatus(order) === "paid" && getFulfillmentStatus(order) === "pending" && (
                        <Button className="order-history-link clear" onClick={() => handleShowCancelModal()}>
                          Cancel Order
                        </Button>
                      )}
                      {order.tracking && getFulfillmentStatus(order) === "shipped" && (
                        <a
                          className="order-history-link clear"
                          href={`https://tools.usps.com/go/TrackConfirmAction?tLabels=${order.tracking}`}
                          target="_blank"
                        >
                          Track Package
                        </a>
                      )}
                      {showCreateReturnButton(order) && (
                        <Button className="order-history-link clear" onClick={() => navigate("/return", { state: { order } })}>
                          Create Return
                        </Button>
                      )}
                      {order.returns?.length > 0 && (
                        <Button className="order-history-link clear" onClick={() => handleViewReturns(order)}>
                          View Returns
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                <CustomerShipping>
                  {order?.shipping && (
                    <div>
                      <div>
                        <strong>Shipping Address</strong>
                      </div>
                      <div>{order.shipping.name}</div>
                      <div>{order.shipping.address.line1}</div>
                      {order.shipping.address.line2 && <div>{order.shipping.address.line2}</div>}
                      <div>
                        {order.shipping.address.city} {order.shipping.address.state} {order.shipping.address.postal_code}{" "}
                      </div>
                    </div>
                  )}
                  {order?.billing && (
                    <div>
                      <div>
                        <strong>Billing Details</strong>
                      </div>
                      <div>{order.billing.name}</div>
                      <div>{order.billing.address.line1}</div>
                      {order.billing.address.line2 && <div>{order.billing.address.line2}</div>}
                      <div>
                        {order.billing.address.city} {order.billing.address.state} {order.billing.address.postal_code}{" "}
                      </div>
                    </div>
                  )}
                </CustomerShipping>

                <div className="customer-products box-padding">
                  {order.products.map((product) => {
                    const details = data.getProduct(product.productId);
                    return (
                      <div className="product-row">
                        <div className="product-image">
                          <picture>
                            <source srcSet={product.thumbnail} type="image/avif" />
                            <img src={product.thumbnail} width="140" />
                          </picture>
                        </div>
                        <div className="product-block">
                          <h2>{product.productName}</h2>
                          <div className="product-block-row">
                            <span className="product-block-label">Design</span>
                            <strong className="product-block-value">{product.design}</strong>
                          </div>
                          <div className="product-block-row">
                            <span className="product-block-label">Size</span>
                            <strong className="product-block-value">{product.size}</strong>
                          </div>
                          <div className="product-block-row">
                            <span className="product-block-label">Qty</span>
                            <strong className="product-block-value">{product.quantity}</strong>
                          </div>
                          <div className="product-block-row">
                            <span className="product-block-label">Price</span>
                            <strong className="product-block-value">${product.productCost / 100}</strong>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="order-footer box-padding">
                  <div className="product-row">
                    <div className="payment-method">
                      <div>
                        <strong>Payment Method</strong>
                      </div>
                      <div>
                        {order.billing.card.brand} ({order.billing.card.last4})
                      </div>
                    </div>
                    <div className="product-block">
                      <div className="product-block-row">
                        <span className="product-block-label">Subtotal</span>
                        <strong className="product-block-value">${(order.subTotal / 100).toFixed(2)}</strong>
                      </div>
                      <div className="product-block-row">
                        <span className="product-block-label">Shipping</span>
                        <strong className="product-block-value">${(order.shippingRate / 100).toFixed(2)}</strong>
                      </div>
                      {order.salesTax > 0 && (
                        <div className="product-block-row">
                          <span className="product-block-label">Sales Tax</span>
                          <strong className="product-block-value">${(order.salesTax / 100).toFixed(2)}</strong>
                        </div>
                      )}
                      <div className="product-block-row">
                        <span className="product-block-label">Total</span>
                        <strong className="product-block-value">${(order.total / 100).toFixed(2)}</strong>
                      </div>
                    </div>
                  </div>
                </div>
                <Modal size={"lg"} fullscreen={"md-down"} show={showReturnsModal} onHide={() => setShowReturnsModal(false)}>
                  <ViewReturnsTemplate currentOrder={order} />
                </Modal>
                <Modal
                  size={"sm"}
                  fullscreen={"sm-down"}
                  show={showCancelModal}
                  onHide={() => setShowCancelModal(false)}
                  className="view-cancel-modal"
                >
                  <Wrapper className="modal-styles cancel-order">
                    <Modal.Header closeButton></Modal.Header>
                    <Modal.Body>
                      {showCancelConfirmation ? (
                        <div className="cancel-order-confirmation">
                          <h2>Order Cancelled</h2>
                          <p>Your refund is being processed.</p>
                          <p className="small-print">Please allow 3-5 business days for it to appear in your account.</p>
                        </div>
                      ) : (
                        <PageTemplate>
                          <div className="cancel-modal-content">
                            <span>Are you sure?</span>
                            <Button onClick={() => handleOrderCancellation()}>Cancel Order</Button>
                          </div>
                        </PageTemplate>
                      )}
                    </Modal.Body>
                  </Wrapper>
                </Modal>
              </div>
            ) : (
              <div>
                <strong>No order found.</strong>
              </div>
            ))}
        </div>
      </PageTemplate>
    </>
  );
};

export default OrderLookup;
